import { createContext, useContext, useEffect, useRef, useCallback, useState } from "react";
import { WS_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RealtimeContext = createContext({ subscribe: () => () => {}, connEpoch: 0 });

export const useRealtime = () => useContext(RealtimeContext);

const PING_MS = 25000; // §0 ground rule: ping every 25s (presence + push gating), 2 missed pongs = dead socket

export function RealtimeProvider({ children }) {
  const { token } = useAuth();
  const subscribers = useRef(new Set());
  const wsRef = useRef(null);
  const [connEpoch, setConnEpoch] = useState(0); // bumps on every (re)connect → run catch-up (heal-queue etc.)

  const subscribe = useCallback((cb) => {
    subscribers.current.add(cb);
    return () => subscribers.current.delete(cb);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let closed = false;
    let retryTimer = null;
    let pingTimer = null;

    const connect = () => {
      if (closed) return;
      let ws;
      try {
        ws = new WebSocket(`${WS_URL}?token=${token}`);
      } catch {
        retryTimer = setTimeout(connect, 3000);
        return;
      }
      wsRef.current = ws;
      let missedPongs = 0;

      ws.onopen = () => {
        missedPongs = 0;
        setConnEpoch((e) => e + 1);
        pingTimer = setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          if (missedPongs >= 2) {
            try { ws.close(); } catch { /* noop */ }
            return;
          }
          missedPongs += 1;
          try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* noop */ }
        }, PING_MS);
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data?.type === "pong") { missedPongs = 0; return; }
          subscribers.current.forEach((cb) => cb(data));
        } catch {
          /* ignore malformed frames */
        }
      };
      ws.onclose = () => {
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = null;
        if (!closed) retryTimer = setTimeout(connect, 2500);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* noop */
        }
      };
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (pingTimer) clearInterval(pingTimer);
      try {
        wsRef.current?.close();
      } catch {
        /* noop */
      }
    };
  }, [token]);

  return (
    <RealtimeContext.Provider value={{ subscribe, connEpoch }}>
      {children}
    </RealtimeContext.Provider>
  );
}
